import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  type DomainStatus,
  type ProvisioningStatus,
  type WorkspaceStatus,
} from "@nahal/api-client";
import { toast } from "@nahal/ui";
import { api } from "./api";

export const keys = {
  search: (term: string) => ["search", term] as const,
  organization: (id: string) => ["organization", id] as const,
  health: (id: string) => ["health", id] as const,
  incidents: (id: string) => ["incidents", id] as const,
  incidentInbox: ["incident-inbox"] as const,
  notes: (id: string) => ["notes", id] as const,
  requests: (status?: string) => ["requests", status ?? "all"] as const,
  queueSummary: ["queue-summary"] as const,
  products: ["products"] as const,
  plans: (productCode?: string) => ["plans", productCode ?? "all"] as const,
  audit: (page: number) => ["audit", page] as const,
  domains: ["domains"] as const,
  workspaceModules: (workspaceId: string) => ["workspace-modules", workspaceId] as const,
};

export const useSearch = (term: string) =>
  useQuery({
    queryKey: keys.search(term),
    queryFn: () => api.search(term),
  });

export const useOrganization = (id: string) =>
  useQuery({ queryKey: keys.organization(id), queryFn: () => api.organization(id) });

export const useHealth = (id: string) =>
  useQuery({ queryKey: keys.health(id), queryFn: () => api.health(id) });

export const useIncidents = (id: string) =>
  useQuery({ queryKey: keys.incidents(id), queryFn: () => api.incidents(id) });

export const useNotes = (id: string) =>
  useQuery({ queryKey: keys.notes(id), queryFn: () => api.notes(id) });

export const useIncidentInbox = () =>
  useQuery({ queryKey: keys.incidentInbox, queryFn: api.incidentInbox });

export const useRequests = (status?: ProvisioningStatus) =>
  useQuery({ queryKey: keys.requests(status), queryFn: () => api.requests(status) });

export const useQueueSummary = () =>
  useQuery({ queryKey: keys.queueSummary, queryFn: api.queueSummary });

export const useProducts = () => useQuery({ queryKey: keys.products, queryFn: api.products });

export const usePlans = (productCode?: string) =>
  useQuery({ queryKey: keys.plans(productCode), queryFn: () => api.plans(productCode) });

export const useAudit = (page: number) =>
  useQuery({ queryKey: keys.audit(page), queryFn: () => api.audit(page) });

function useApiMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  options: { invalidate?: ReadonlyArray<readonly unknown[]>; success?: string } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      options.invalidate?.forEach((key) => queryClient.invalidateQueries({ queryKey: [...key] }));
      if (options.success) toast.success(options.success);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        options.invalidate?.forEach((key) => queryClient.invalidateQueries({ queryKey: [...key] }));
      }
      toast.error(error instanceof ApiError ? error.message : "Something went wrong.");
    },
  });
}

export const useDecide = () =>
  useApiMutation(
    (args: { requestId: string; decision: "approve" | "reject" | "complete"; note?: string }) =>
      api.decide(args.requestId, args.decision, args.note),
    { invalidate: [["requests"], keys.queueSummary], success: "Request updated." },
  );

export const useCreateWorkspace = (organizationId: string) =>
  useApiMutation(
    (args: {
      organizationId: string;
      productCode: string;
      code: string;
      name: string;
      planCode?: string;
    }) => api.createWorkspace(args),
    { invalidate: [keys.organization(organizationId)], success: "Workspace provisioning started." },
  );

export const useChangeWorkspaceStatus = (organizationId: string) =>
  useApiMutation(
    (args: { workspaceId: string; status: WorkspaceStatus }) =>
      api.changeWorkspaceStatus(args.workspaceId, args.status),
    { invalidate: [keys.organization(organizationId)], success: "Workspace updated." },
  );

export const useSetEntitlements = (organizationId: string) =>
  useApiMutation(
    (args: { workspaceId: string; entitlements: Record<string, string> }) =>
      api.setEntitlements(args.workspaceId, args.entitlements),
    { invalidate: [keys.organization(organizationId)], success: "Entitlements updated." },
  );

export const useCreateCompany = (organizationId: string) =>
  useApiMutation(
    (args: {
      workspaceId: string;
      code: string;
      name: string;
      nameBn?: string;
      phone1?: string;
      isGroup?: boolean;
    }) => api.createCompany(args.workspaceId, args),
    { invalidate: [keys.organization(organizationId)], success: "Company created in the product." },
  );

export const useCreateMarketplace = (organizationId: string) =>
  useApiMutation(
    (args: {
      workspaceId: string;
      code: string;
      name: string;
      nameBn?: string;
      domain?: string;
      appCode?: string;
    }) => api.createMarketplace(args.workspaceId, args),
    { invalidate: [keys.organization(organizationId)], success: "Marketplace created in the product." },
  );

export const useAddNote = (organizationId: string) =>
  useApiMutation(
    (args: { body: string; pinned: boolean }) => api.addNote(organizationId, args),
    { invalidate: [keys.notes(organizationId)], success: "Note added." },
  );

export const useOpsDomains = () => useQuery({ queryKey: keys.domains, queryFn: api.domains });

export const useChangeDomainStatus = () =>
  useApiMutation(
    (args: { domainId: string; status: DomainStatus }) =>
      api.changeDomainStatus(args.domainId, args.status),
    { invalidate: [keys.domains], success: "Domain updated." },
  );

export const useWorkspaceModules = (workspaceId: string) =>
  useQuery({
    queryKey: keys.workspaceModules(workspaceId),
    queryFn: () => api.workspaceModules(workspaceId),
    enabled: Boolean(workspaceId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useSetWorkspaceModules = (organizationId: string, workspaceId: string) =>
  useApiMutation(
    (body: Parameters<typeof api.setWorkspaceModules>[1]) => api.setWorkspaceModules(workspaceId, body),
    { invalidate: [keys.workspaceModules(workspaceId), keys.organization(organizationId)], success: "Module grants saved. Check the application status below." },
  );

export const useRetryWorkspaceModules = (organizationId: string, workspaceId: string) =>
  useApiMutation(
    (expectedRevision: number) => api.retryWorkspaceModules(workspaceId, expectedRevision),
    { invalidate: [keys.workspaceModules(workspaceId), keys.organization(organizationId)], success: "Module application retried. Check the status below." },
  );

export function useModuleFeature() {
  const features = useQuery({
    queryKey: ["platform-features"],
    queryFn: () => api.features().catch(() => ({ moduleAccessEnabled: false })),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    networkMode: "always",
  });
  return features.data?.moduleAccessEnabled === true && !features.isError;
}
